/**
 * FtpSourceList - Photographer FTP upload credentials
 *
 * Features:
 * - Table of all FTP sources
 * - Create/Edit with auto-generated credentials
 * - Active/inactive toggle
 * - Safe reload (wait for uploads)
 * - Copy credentials to clipboard
 */

import { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import {
  fetchFtpSources,
  createFtpSource,
  updateFtpSource,
  deleteFtpSource,
  reloadFtpSource,
  fetchPhotographers,
} from '../../../../api';
import Button from '../../../../shared/components/ui/Button';
import Modal from '../../../../shared/components/ui/Modal';
import Spinner from '../../../../shared/components/ui/Spinner';
import styles from './FtpSourceList.module.css';

function generatePassword(length = 16) {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

function FtpSourceModal({ source, photographers, onSave, onClose, isNew = false, onShowMessage }) {
  const [form, setForm] = useState({
    name: source?.name || '',
    photographer_id: source?.photographer_id || '',
    username: source?.username || '',
    password: isNew ? generatePassword() : source?.password || '',
    folder_path: source?.folder_path || '',
    is_active: source?.is_active !== false,
    notes: source?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(isNew);

  const handleSave = async () => {
    if (!form.name.trim() || !form.username.trim()) return;
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      console.error('Failed to save FTP source:', err);
    } finally {
      setSaving(false);
    }
  };

  const regeneratePassword = () => {
    setForm((prev) => ({ ...prev, password: generatePassword() }));
    setShowPassword(true);
  };

  const copyCredentials = () => {
    const text = `FTP Credentials for ${form.name}\nHost: ftp.yourserver.com\nUsername: ${form.username}\nPassword: ${form.password}\nFolder: ${form.folder_path || '/'}`;
    navigator.clipboard.writeText(text);
    if (onShowMessage) {
      onShowMessage('Credentials copied to clipboard');
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose}>
      <Modal.Header onClose={onClose}>
        {isNew ? 'Create FTP Source' : 'Edit FTP Source'}
      </Modal.Header>
      <Modal.Body>
        <div className={styles.formGroup}>
          <label className={styles.label}>Source Name</label>
          <input
            type="text"
            className={styles.input}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g., John's Camera"
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Photographer</label>
          <select
            className={styles.select}
            value={form.photographer_id}
            onChange={(e) => setForm({ ...form, photographer_id: e.target.value })}
          >
            <option value="">-- Select Photographer --</option>
            {photographers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.username}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Username</label>
            <input
              type="text"
              className={styles.input}
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              placeholder="ftp_username"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Password</label>
            <div className={styles.passwordField}>
              <input
                type={showPassword ? 'text' : 'password'}
                className={styles.input}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="********"
              />
              <button
                type="button"
                className={styles.togglePassword}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
        </div>

        <div className={styles.passwordActions}>
          <Button variant="ghost" size="sm" onClick={regeneratePassword}>
            Generate New Password
          </Button>
          <Button variant="ghost" size="sm" onClick={copyCredentials}>
            Copy Credentials
          </Button>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Folder Path</label>
          <input
            type="text"
            className={styles.input}
            value={form.folder_path}
            onChange={(e) => setForm({ ...form, folder_path: e.target.value })}
            placeholder="/uploads/john"
          />
          <p className={styles.hint}>
            Folder where uploaded files will be stored
          </p>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className={styles.checkbox}
            />
            <span>Active</span>
          </label>
          <p className={styles.hint}>
            Inactive sources cannot upload files
          </p>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Notes</label>
          <textarea
            className={styles.textarea}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Internal notes..."
            rows={2}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving || !form.name.trim() || !form.username.trim()}
        >
          {(() => {
            if (saving) return 'Saving...';
            if (isNew) return 'Create';
            return 'Save';
          })()}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default function FtpSourceList() {
  // Parse query params from hash (e.g., company/ftp-sources?action=new)
  const getHashParams = () => new URLSearchParams(window.location.hash.split('?')[1] || '');

  const [sources, setSources] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  // Modal state
  const [showModal, setShowModal] = useState(
    getHashParams().get('action') === 'new'
  );
  const [editingSource, setEditingSource] = useState(null);
  const [isNewSource, setIsNewSource] = useState(true);

  // Reload state
  const [reloading, setReloading] = useState({});
  // Confirmation/alert modal states (replaces browser confirm/alert)
  const [confirmDeleteSource, setConfirmDeleteSource] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sourcesData, photographersData] = await Promise.all([
        fetchFtpSources({ limit: 200 }),
        fetchPhotographers({ limit: 200 }),
      ]);
      setSources(sourcesData.results || sourcesData || []);
      setPhotographers(photographersData.results || photographersData || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredSources = sources.filter((s) => {
    // Search filter
    if (search.trim()) {
      const term = search.toLowerCase();
      if (
        !s.name?.toLowerCase().includes(term) &&
        !s.username?.toLowerCase().includes(term)
      ) {
        return false;
      }
    }

    // Active filter
    if (activeFilter === 'active' && !s.is_active) return false;
    if (activeFilter === 'inactive' && s.is_active) return false;

    return true;
  });

  const handleCreate = () => {
    setEditingSource(null);
    setIsNewSource(true);
    setShowModal(true);
  };

  const handleEdit = (source) => {
    setEditingSource(source);
    setIsNewSource(false);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    if (isNewSource) {
      await createFtpSource(formData);
    } else if (editingSource) {
      await updateFtpSource(editingSource.id, formData);
    }
    loadData();
  };

  const handleDelete = (source) => {
    setConfirmDeleteSource(source);
  };

  const confirmDeleteSourceAction = async () => {
    if (!confirmDeleteSource) return;
    const source = confirmDeleteSource;
    setConfirmDeleteSource(null);
    try {
      await deleteFtpSource(source.id);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleReload = async (source) => {
    setReloading((prev) => ({ ...prev, [source.id]: true }));
    try {
      await reloadFtpSource(source.id);
      setSuccessMessage('FTP source reloaded successfully. New credentials are now active.');
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setReloading((prev) => ({ ...prev, [source.id]: false }));
    }
  };

  const getPhotographerName = (id) => {
    const p = photographers.find((p) => p.id === id);
    return p?.username || 'Unassigned';
  };

  if (loading && sources.length === 0) {
    return (
      <div className={styles.loading}>
        <Spinner />
        <p>Loading FTP sources...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>FTP Sources</h2>
          <p className={styles.subtitle}>
            Photographer upload credentials and folders
          </p>
        </div>
        <Button variant="primary" onClick={handleCreate}>
          + New Source
        </Button>
      </div>

      {error && (
        <div className={styles.error}>
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filters}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search sources..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className={styles.filterSelect}
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value)}
        >
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        <span className={styles.count}>{filteredSources.length} sources</span>
      </div>

      {/* Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Name</th>
              <th className={styles.th}>Username</th>
              <th className={styles.th}>Photographer</th>
              <th className={styles.th}>Folder</th>
              <th className={styles.th}>Status</th>
              <th className={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSources.map((source) => (
              <tr key={source.id} className={styles.row}>
                <td className={styles.td}>
                  <span className={styles.sourceName}>{source.name}</span>
                </td>
                <td className={styles.td}>
                  <code className={styles.username}>{source.username}</code>
                </td>
                <td className={styles.td}>
                  {getPhotographerName(source.photographer_id)}
                </td>
                <td className={styles.td}>
                  <code className={styles.folderPath}>
                    {source.folder_path || '/'}
                  </code>
                </td>
                <td className={styles.td}>
                  <span
                    className={clsx(
                      styles.statusBadge,
                      source.is_active ? styles.active : styles.inactive
                    )}
                  >
                    {source.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className={styles.td}>
                  <div className={styles.actions}>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(source)}>
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleReload(source)}
                      disabled={reloading[source.id]}
                    >
                      {reloading[source.id] ? 'Reloading...' : 'Reload'}
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => handleDelete(source)}>
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredSources.length === 0 && (
              <tr>
                <td colSpan={6} className={styles.empty}>
                  No FTP sources found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <FtpSourceModal
          source={editingSource}
          photographers={photographers}
          isNew={isNewSource}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditingSource(null);
          }}
          onShowMessage={setSuccessMessage}
        />
      )}

      {/* Success Message Modal (replaces browser alert) */}
      <Modal
        isOpen={!!successMessage}
        onClose={() => setSuccessMessage(null)}
        size="sm"
      >
        <Modal.Header onClose={() => setSuccessMessage(null)}>
          Success
        </Modal.Header>
        <Modal.Body>
          <p>{successMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setSuccessMessage(null)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm Delete Source Modal */}
      <Modal
        isOpen={!!confirmDeleteSource}
        onClose={() => setConfirmDeleteSource(null)}
        size="sm"
      >
        <Modal.Header onClose={() => setConfirmDeleteSource(null)}>
          Confirm Delete
        </Modal.Header>
        <Modal.Body>
          <p>Delete FTP source "{confirmDeleteSource?.name}"?</p>
          <p style={{ marginTop: 8, fontSize: 13, color: '#999' }}>
            This cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => setConfirmDeleteSource(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteSourceAction}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
